import { useMemo, useState } from 'react'
import axios from 'axios'
import {
    Accordion,
    AccordionDetails,
    AccordionGroup,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormLabel,
    Input,
    Modal,
    ModalDialog,
    Option,
    Select,
    Sheet,
    Stack,
    Table,
    Textarea,
    Typography,
} from '@mui/joy'
import SearchIcon from '@mui/icons-material/Search'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningIcon from '@mui/icons-material/Warning'
import ReplayIcon from '@mui/icons-material/Replay'
import BlockIcon from '@mui/icons-material/Block'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import { phase2MigrationApi, Phase2Divergence, Phase2ExecuteResult, Phase2McGroup, Phase2PreviewApp, Phase2PreviewResponse, Phase2Warning } from './phase2-api'

/**
 * Surface the backend's `{message}` payload on axios errors. axios's own
 * `err.message` is "Request failed with status code 4xx" which hides the real
 * cause from the admin running the migration.
 */
function extractErrorMessage(err: unknown, fallback: string): string {
    if (axios.isAxiosError(err)) {
        const data = err.response?.data
        if (data && typeof data === 'object' && 'message' in data && typeof data.message === 'string') {
            return data.message
        }
        return err.message
    }
    return err instanceof Error ? err.message : fallback
}

/**
 * Backend doesn't ship a `newAppName` (it's generated at execute-time inside the txn,
 * including a timestamp + ObjectId suffix). For preview rendering we show a stable
 * humanized hint so admins know what the App will look like, without pretending we
 * know the final name. Format mirrors `cleanProjectName(`${name}_${mc}`)` intent
 * minus the random suffix.
 */
const computePreviewAppName = (sampleName: string | undefined, mc: string, fallback: string): string => {
    const base = (sampleName || fallback || '').trim()
    if (!base) return `app_${mc}`
    return `${base}_${mc}`
}

const McGroupRow = ({ group, isAnchor, previewAppName }: { group: Phase2McGroup; isAnchor: boolean; previewAppName: string | null }) => (
    <tr>
        <td>
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography level="body-sm" fontWeight="md">
                    {group.mc || '—'}
                </Typography>
                {isAnchor ? (
                    <Chip size="sm" variant="solid" color="primary">
                        Anchor
                    </Chip>
                ) : (
                    <Chip size="sm" variant="soft" color="warning">
                        Split
                    </Chip>
                )}
            </Stack>
            {!isAnchor && previewAppName && (
                <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                    New app: {previewAppName}
                </Typography>
            )}
        </td>
        <td>
            {group.emails.length === 0 ? (
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                    —
                </Typography>
            ) : (
                <Stack spacing={0.25}>
                    {group.emails.map((email) => (
                        <Typography key={email} level="body-xs" sx={{ fontFamily: 'monospace', fontSize: '11px' }}>
                            {email}
                        </Typography>
                    ))}
                </Stack>
            )}
        </td>
        <td>
            <Stack spacing={0.25}>
                <Typography level="body-xs" fontWeight="md">
                    {group.sampleCompany.name || '—'}
                </Typography>
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                    MC {group.sampleCompany.mcNumber || '—'} · DOT {group.sampleCompany.dotNumber || '—'}
                </Typography>
            </Stack>
        </td>
    </tr>
)

const ApplicationCard = ({ app }: { app: Phase2PreviewApp }) => (
    <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <Chip variant="solid" color="neutral" size="sm">
                    Application
                </Chip>
                <Typography level="title-sm">{app.projectName || app.appId}</Typography>
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                    {app.mcGroups.length} MC group{app.mcGroups.length !== 1 ? 's' : ''} · {app.expectedSplits} new app
                    {app.expectedSplits !== 1 ? 's' : ''}
                </Typography>
            </Stack>
            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'hidden' }}>
                <Table size="sm" stripe="odd" hoverRow>
                    <thead>
                        <tr>
                            <th style={{ width: '25%' }}>MC</th>
                            <th style={{ width: '40%' }}>Emails</th>
                            <th style={{ width: '35%' }}>Sample company</th>
                        </tr>
                    </thead>
                    <tbody>
                        {app.mcGroups.map((mcGroup) => {
                            const isAnchor = mcGroup.mc === app.anchorMc
                            const previewAppName = isAnchor ? null : computePreviewAppName(mcGroup.sampleCompany.name, mcGroup.mc, app.projectName)
                            return <McGroupRow key={`${app.appId}-${mcGroup.mc}`} group={mcGroup} isAnchor={isAnchor} previewAppName={previewAppName} />
                        })}
                    </tbody>
                </Table>
            </Sheet>
        </CardContent>
    </Card>
)

/**
 * Build the per-warning options list from the backend's divergence payload. Backend
 * doesn't ship `options` directly on the warning — we derive them from the matching
 * divergence entry on the originating App. Returns the full {value, emails} shape so
 * the UI can show how many users currently use each value.
 */
type WarningOption = { value: string; emails: string[] }

const findWarningOptions = (warning: Phase2Warning, apps: Phase2PreviewApp[]): WarningOption[] => {
    const app = apps.find((a) => a.appId === warning.appId)
    if (!app) return []
    const divergences: Phase2Divergence[] | undefined = warning.type === 'aiAgentDivergence' ? app.aiAgentDivergences : app.transferNumberDivergences
    const match = divergences?.find((d) => d.mcNumber === warning.mc)
    return match ? match.values.map((v) => ({ value: v.value, emails: v.emails })) : []
}

const WarningAccordion = ({
    warning,
    options,
    chosenValue,
    onChange,
}: {
    warning: Phase2Warning
    options: WarningOption[]
    chosenValue: string | undefined
    onChange: (value: string) => void
}) => (
    <Accordion>
        <AccordionSummary>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                <WarningIcon sx={{ color: 'warning.500', fontSize: 18 }} />
                <Typography level="title-sm">{warning.type === 'aiAgentDivergence' ? 'AI agent divergence' : 'Transfer number divergence'}</Typography>
                <Chip size="sm" variant="soft" color={chosenValue ? 'success' : 'warning'}>
                    {chosenValue ? 'Resolved' : 'Action required'}
                </Chip>
                {warning.mc && (
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        MC {warning.mc}
                    </Typography>
                )}
            </Stack>
        </AccordionSummary>
        <AccordionDetails>
            <Stack spacing={1.5}>
                <Typography level="body-sm">{warning.description}</Typography>
                <FormControl>
                    <FormLabel>Chosen value</FormLabel>
                    {options.length > 0 ? (
                        <Select value={chosenValue ?? ''} onChange={(_, val) => onChange(val ?? '')} placeholder="Choose value" name={`warning-${warning.id}`}>
                            {options.map((opt) => (
                                <Option key={opt.value} value={opt.value}>
                                    {opt.value} ({opt.emails.length} email{opt.emails.length === 1 ? '' : 's'})
                                </Option>
                            ))}
                        </Select>
                    ) : (
                        <Input
                            type="text"
                            value={chosenValue ?? ''}
                            onChange={(e) => onChange(e.target.value)}
                            placeholder="Enter chosen value"
                            name={`warning-${warning.id}`}
                            autoComplete="off"
                        />
                    )}
                </FormControl>
            </Stack>
        </AccordionDetails>
    </Accordion>
)

export const Phase2MigrationPage = () => {
    const [ownerEmail, setOwnerEmail] = useState('')
    const [submittedOwnerEmail, setSubmittedOwnerEmail] = useState('')
    const [preview, setPreview] = useState<Phase2PreviewResponse | null>(null)
    const [loadingPreview, setLoadingPreview] = useState(false)
    const [previewError, setPreviewError] = useState('')

    const [acknowledgedWarnings, setAcknowledgedWarnings] = useState<Record<string, string>>({})

    const [executing, setExecuting] = useState(false)
    const [executeResult, setExecuteResult] = useState<Phase2ExecuteResult | null>(null)
    const [executeError, setExecuteError] = useState('')
    const [confirmOpen, setConfirmOpen] = useState(false)

    const [disableOpen, setDisableOpen] = useState(false)
    const [disableReason, setDisableReason] = useState('')
    const [disabling, setDisabling] = useState(false)
    const [disableError, setDisableError] = useState('')
    const [disableSuccess, setDisableSuccess] = useState('')

    const allWarningsAcknowledged = useMemo(() => {
        if (!preview) return false
        return preview.warnings.every((w) => {
            const value = acknowledgedWarnings[w.id]
            return typeof value === 'string' && value.trim().length > 0
        })
    }, [preview, acknowledgedWarnings])

    const totalSplits = useMemo(() => {
        if (!preview) return 0
        return preview.apps.reduce((sum, app) => sum + app.expectedSplits, 0)
    }, [preview])

    const ownerEmailDirty = ownerEmail.trim() !== submittedOwnerEmail

    const handlePreview = async () => {
        if (loadingPreview) return
        const email = ownerEmail.trim()
        if (!email) return
        setLoadingPreview(true)
        setPreviewError('')
        setPreview(null)
        setAcknowledgedWarnings({})
        setExecuteResult(null)
        setExecuteError('')
        setDisableSuccess('')
        try {
            const data = await phase2MigrationApi.getPreview(email)
            setPreview(data)
            setSubmittedOwnerEmail(email)
            if (data.apps.length === 0 && !data.alreadyMigrated) {
                setPreviewError('Owner has no applications to migrate.')
            }
        } catch (err: unknown) {
            setPreviewError(extractErrorMessage(err, 'Preview failed'))
        } finally {
            setLoadingPreview(false)
        }
    }

    const handleExecute = async () => {
        if (!preview) return
        setExecuting(true)
        setExecuteError('')
        try {
            const trimmed: Record<string, string> = {}
            for (const w of preview.warnings) {
                const value = acknowledgedWarnings[w.id]
                if (value !== undefined) trimmed[w.id] = value.trim()
            }
            const data = await phase2MigrationApi.executeMigration(submittedOwnerEmail, trimmed)
            setExecuteResult(data)
        } catch (err: unknown) {
            setExecuteError(extractErrorMessage(err, 'Execution failed'))
            // M4: re-fetch preview after a failure so the next attempt sees the actual current state.
            // If the migration committed but post-checks/Stripe-init logged a non-fatal failure, the
            // refreshed preview will show alreadyMigrated=true and the UI will route to Disable flag
            // / verification instead of letting the admin click Run again and hit "already migrated".
            // Best-effort: a preview fetch failure here doesn't override the original execute error.
            try {
                const refreshed = await phase2MigrationApi.getPreview(submittedOwnerEmail)
                setPreview(refreshed)
            } catch {
                // ignore — the execute error above is what the admin needs to act on
            }
        } finally {
            setExecuting(false)
        }
    }

    const handleDisableFlag = async () => {
        if (!preview) return
        const reason = disableReason.trim()
        if (!reason) {
            setDisableError('Reason is required.')
            return
        }
        setDisabling(true)
        setDisableError('')
        setDisableSuccess('')
        try {
            await phase2MigrationApi.disableFlag(submittedOwnerEmail, reason)
            setDisableSuccess(`Flag disabled for organization ${preview.organization.id}.`)
            setDisableOpen(false)
            setDisableReason('')
        } catch (err: unknown) {
            setDisableError(extractErrorMessage(err, 'Disable flag failed'))
        } finally {
            setDisabling(false)
        }
    }

    return (
        <Box>
            <Typography level="h4" sx={{ mb: 0.5 }}>
                Phase 2 — Per-MC Application Split
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.tertiary', mb: 3 }}>
                Preview how an owner&apos;s Application(s) will be split into per-MC Applications, resolve any divergence warnings, then execute the migration.
            </Typography>

            {/* Lookup */}
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Typography level="title-md" sx={{ mb: 2 }}>
                        Owner lookup
                    </Typography>
                    <Stack direction="row" spacing={1.5} alignItems="flex-end">
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Owner Email</FormLabel>
                            <Input
                                value={ownerEmail}
                                onChange={(e) => setOwnerEmail(e.target.value)}
                                placeholder="owner@example.com"
                                name="phase2-owner-email"
                                type="email"
                                autoComplete="new-password"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !loadingPreview) handlePreview()
                                }}
                            />
                        </FormControl>
                        <Button loading={loadingPreview} onClick={handlePreview} variant="solid" color="primary" startDecorator={<SearchIcon />}>
                            Preview
                        </Button>
                    </Stack>
                    {ownerEmailDirty && submittedOwnerEmail && (
                        <Typography level="body-xs" sx={{ color: 'warning.600', mt: 1 }}>
                            Email changed since preview — click Preview again to refresh.
                        </Typography>
                    )}
                    {previewError && (
                        <Alert color="danger" variant="soft" sx={{ mt: 1.5 }} startDecorator={<WarningIcon />}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                                <Typography level="body-sm" sx={{ flex: 1 }}>
                                    {previewError}
                                </Typography>
                                <Button size="sm" variant="outlined" color="danger" onClick={handlePreview} startDecorator={<ReplayIcon />}>
                                    Retry
                                </Button>
                            </Stack>
                        </Alert>
                    )}
                </CardContent>
            </Card>

            {/* Preview body */}
            {preview && (
                <>
                    {preview.alreadyMigrated && (
                        <Alert color="warning" variant="soft" sx={{ mb: 3 }} startDecorator={<CheckCircleIcon />}>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography level="title-sm">Already migrated</Typography>
                                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                        This organization is already on Multi-MC Phase 2. Use the rollback section below to disable the flag.
                                    </Typography>
                                </Box>
                                <Button size="sm" variant="outlined" color="warning" onClick={() => setDisableOpen(true)} startDecorator={<BlockIcon />}>
                                    Disable flag
                                </Button>
                            </Stack>
                        </Alert>
                    )}

                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Chip variant="soft" color="primary" size="md">
                            {totalSplits} new Application{totalSplits !== 1 ? 's' : ''} will be created
                        </Chip>
                        <Chip variant="soft" color="neutral" size="md">
                            {preview.apps.length} source App{preview.apps.length !== 1 ? 's' : ''}
                        </Chip>
                    </Stack>

                    {preview.apps.map((app) => (
                        <ApplicationCard key={app.appId} app={app} />
                    ))}

                    {preview.warnings.length > 0 && (
                        <Card variant="outlined" sx={{ mb: 3, borderColor: 'warning.400' }}>
                            <CardContent>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                                    <WarningIcon sx={{ color: 'warning.500' }} />
                                    <Typography level="title-md">Warnings to acknowledge</Typography>
                                    <Chip size="sm" variant="soft" color={allWarningsAcknowledged ? 'success' : 'warning'}>
                                        {Object.values(acknowledgedWarnings).filter((v) => v && v.trim().length > 0).length}/{preview.warnings.length}
                                    </Chip>
                                </Stack>
                                <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 1.5 }}>
                                    Each warning must have a chosen value before you can run the migration.
                                </Typography>
                                <AccordionGroup>
                                    {preview.warnings.map((w) => (
                                        <WarningAccordion
                                            key={w.id}
                                            warning={w}
                                            options={findWarningOptions(w, preview.apps)}
                                            chosenValue={acknowledgedWarnings[w.id]}
                                            onChange={(value) =>
                                                setAcknowledgedWarnings((prev) => ({
                                                    ...prev,
                                                    [w.id]: value,
                                                }))
                                            }
                                        />
                                    ))}
                                </AccordionGroup>
                            </CardContent>
                        </Card>
                    )}

                    {preview.apps.length > 0 && !preview.alreadyMigrated && !executeResult && (
                        <Card variant="outlined" sx={{ mb: 3 }}>
                            <CardContent>
                                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Typography level="title-sm">Confirm and run migration</Typography>
                                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                            This will create per-MC Applications, propagate users, and migrate the subscription to org level. The action is
                                            logged.
                                        </Typography>
                                    </Box>
                                    <Button
                                        disabled={!allWarningsAcknowledged || executing || ownerEmailDirty}
                                        loading={executing}
                                        color="primary"
                                        variant="solid"
                                        startDecorator={<RocketLaunchIcon />}
                                        onClick={() => setConfirmOpen(true)}
                                    >
                                        Confirm Migrate
                                    </Button>
                                </Stack>
                                {!allWarningsAcknowledged && preview.warnings.length > 0 && (
                                    <Typography level="body-xs" sx={{ color: 'warning.600', mt: 1 }}>
                                        Resolve all warnings above to enable Confirm Migrate.
                                    </Typography>
                                )}
                                {ownerEmailDirty && (
                                    <Typography level="body-xs" sx={{ color: 'warning.600', mt: 1 }}>
                                        Email changed since preview — click Preview again to refresh.
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </>
            )}

            {/* Execute result */}
            {executeResult && (
                <Alert color="success" variant="soft" sx={{ mb: 3 }} startDecorator={<CheckCircleIcon />}>
                    <Box>
                        <Typography level="title-sm">Migration completed</Typography>
                        <Typography level="body-sm">
                            Processed {executeResult.processedApps} App{executeResult.processedApps !== 1 ? 's' : ''}, created {executeResult.splitsCreated}{' '}
                            split
                            {executeResult.splitsCreated !== 1 ? 's' : ''}.
                        </Typography>
                        {executeResult.newApplicationIds.length > 0 && (
                            <Sheet variant="soft" sx={{ p: 1, mt: 1, borderRadius: 'sm' }}>
                                <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 0.5 }}>
                                    New Application IDs:
                                </Typography>
                                <Stack spacing={0.25}>
                                    {executeResult.newApplicationIds.map((id) => (
                                        <Typography key={id} level="body-xs" sx={{ fontFamily: 'monospace', fontSize: '11px' }}>
                                            {id}
                                        </Typography>
                                    ))}
                                </Stack>
                            </Sheet>
                        )}
                    </Box>
                </Alert>
            )}
            {executeError && (
                <Alert color="danger" variant="soft" sx={{ mb: 3 }} startDecorator={<WarningIcon />}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                        <Typography level="body-sm" sx={{ flex: 1 }}>
                            {executeError}
                        </Typography>
                        <Button size="sm" variant="outlined" color="danger" onClick={() => setConfirmOpen(true)} startDecorator={<ReplayIcon />}>
                            Retry
                        </Button>
                    </Stack>
                </Alert>
            )}

            {/* Rollback */}
            {preview && !disableSuccess && (
                <Card variant="outlined" sx={{ mb: 3, borderColor: 'danger.300' }}>
                    <CardContent>
                        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                            <Box>
                                <Typography level="title-sm">Rollback</Typography>
                                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                    Disable the Multi-MC Phase 2 flag for this owner&apos;s organization. Audit-logged.
                                </Typography>
                            </Box>
                            <Button color="danger" variant="outlined" startDecorator={<BlockIcon />} onClick={() => setDisableOpen(true)}>
                                Disable flag
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>
            )}
            {disableSuccess && (
                <Alert color="success" variant="soft" sx={{ mb: 3 }} startDecorator={<CheckCircleIcon />}>
                    {disableSuccess}
                </Alert>
            )}

            {/* Confirm migrate dialog */}
            <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <ModalDialog variant="outlined" sx={{ maxWidth: 480, borderRadius: 'lg' }}>
                    <DialogTitle>
                        <RocketLaunchIcon sx={{ color: 'primary.500', mr: 1 }} />
                        Confirm Phase 2 migration
                    </DialogTitle>
                    <Divider />
                    <DialogContent>
                        <Typography level="body-sm" sx={{ mb: 1.5 }}>
                            You are about to split this owner&apos;s Application(s) into per-MC Applications.
                        </Typography>
                        {preview && (
                            <Sheet variant="soft" color="primary" sx={{ p: 1.5, borderRadius: 'sm', mb: 2 }}>
                                <Typography level="body-xs" fontWeight="lg">
                                    Owner
                                </Typography>
                                <Typography level="body-sm">{submittedOwnerEmail}</Typography>
                                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                    {totalSplits} new Applications · {preview.apps.length} source Apps
                                </Typography>
                            </Sheet>
                        )}
                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                            This action is logged with your admin identity.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="plain" color="neutral" onClick={() => setConfirmOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            loading={executing}
                            color="primary"
                            variant="solid"
                            onClick={async () => {
                                setConfirmOpen(false)
                                await handleExecute()
                            }}
                        >
                            Run migration
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>

            {/* Disable flag dialog */}
            <Modal open={disableOpen} onClose={() => setDisableOpen(false)}>
                <ModalDialog variant="outlined" sx={{ maxWidth: 480, borderRadius: 'lg' }}>
                    <DialogTitle>
                        <BlockIcon sx={{ color: 'danger.500', mr: 1 }} />
                        Disable Multi-MC Phase 2 flag
                    </DialogTitle>
                    <Divider />
                    <DialogContent>
                        <Alert color="danger" variant="soft" sx={{ mb: 2 }}>
                            <Typography level="body-sm" fontWeight="lg" sx={{ mb: 1 }}>
                                This rolls back the Phase 2 flag for the organization.
                            </Typography>
                            <Typography level="body-xs" sx={{ mb: 0.5 }}>
                                <strong>What this DOES:</strong> Flips <code>companyFromParentEnabled</code> back to <code>false</code>. Readers immediately
                                resume using per-email <code>companyDetail</code> (preserved by Migration Data Parity).
                            </Typography>
                            <Typography level="body-xs">
                                <strong>What this does NOT do:</strong> Application splits created during migration <strong>are not removed</strong>. The org
                                keeps multiple Apps and the CompanySwitcher stays visible. Full reversal of splits is not currently implemented.
                            </Typography>
                        </Alert>
                        <FormControl>
                            <FormLabel>Reason</FormLabel>
                            <Textarea
                                value={disableReason}
                                onChange={(e) => setDisableReason(e.target.value)}
                                placeholder="Why are you disabling the flag?"
                                minRows={3}
                                name="phase2-disable-reason"
                            />
                        </FormControl>
                        {disableError && (
                            <Alert color="danger" variant="soft" sx={{ mt: 1.5 }} startDecorator={<WarningIcon />}>
                                {disableError}
                            </Alert>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="plain"
                            color="neutral"
                            onClick={() => {
                                setDisableOpen(false)
                                setDisableError('')
                            }}
                        >
                            Cancel
                        </Button>
                        <Button loading={disabling} color="danger" variant="solid" onClick={handleDisableFlag}>
                            Disable flag
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>
        </Box>
    )
}
